import { useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEO from '@/components/base/SEO';
import { testimonials } from '@/mocks/movies';

export default function AboutPage() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [location.pathname]);

  useEffect(() => {
    if (location.hash) {
      const timer = setTimeout(() => {
        const el = document.querySelector(location.hash);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [location.hash]);

  const breadcrumbItems = [
    { name: '首页', url: '/' },
    { name: '关于我们', url: '/about' },
  ];

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '快看影视',
    url: 'https://kuikanys.com',
    description: '高清免费电影在线观看，电影大全不缓冲，最新电影免费秒播，流畅高清不卡顿免费看。',
    foundingDate: '2023',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'service@kuikanys.com',
      availableLanguage: ['Chinese'],
    },
    sameAs: [
      'https://weibo.com/kuikanys',
    ],
  };

  const aboutPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于快看影视 - 高清免费电影在线观看',
    description: '快看影视品牌介绍、口碑评价与联系方式。高清免费电影在线观看平台，电影大全不缓冲，最新电影免费秒播。',
    url: 'https://kuikanys.com/about',
    isPartOf: {
      '@type': 'WebSite',
      name: '快看影视',
      url: 'https://kuikanys.com',
    },
  };

  return (
    <>
      <SEO
        title="关于我们 - 快看影视品牌介绍与联系方式 | 快看影视"
        description="快看影视成立于2023年，是国内领先的高清免费电影在线观看平台。了解我们的品牌故事、用户口碑评价和联系方式。海量免费电影资源，播放流畅不卡顿。"
        keywords="快看影视关于我们,快看影视品牌介绍,快看影视联系方式,免费电影平台"
        canonicalUrl="https://kuikanys.com/about"
      />
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(aboutPageSchema)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="w-full px-4 md:px-6 pt-8 md:pt-10 pb-12">
            <div className="max-w-4xl mx-auto">
              <Breadcrumb items={breadcrumbItems} />

              <section className="mt-6 mb-14">
                <h1 className="text-2xl md:text-4xl font-bold text-foreground-50 font-heading mb-4">
                  关于快看影视
                </h1>
                <div className="h-1 w-16 bg-primary-500 rounded-full mb-6"></div>
                <p className="text-sm md:text-base text-foreground-400 leading-relaxed mb-4">
                  快看影视成立于2023年，是国内领先的<strong>高清免费电影在线观看</strong>平台。我们致力于为广大电影爱好者提供高质量、流畅的观影体验。无论是最新院线大片还是经典老片，在快看影视都能免费观看。
                </p>
                <p className="text-sm md:text-base text-foreground-400 leading-relaxed mb-4">
                  我们的片库涵盖<strong>高清电影</strong>、<strong>最新电影</strong>、<strong>电影大全</strong>等多种类别，包括动作片、喜剧片、科幻片、爱情片、恐怖片、纪录片和动画片等。无论你喜欢什么类型的电影，在快看影视都能找到心仪的影片。
                </p>
                <p className="text-sm md:text-base text-foreground-400 leading-relaxed">
                  快看影视始终坚持"<strong>免费、高清、流畅</strong>"的服务理念。所有电影均可<strong>免费在线观看</strong>，无需注册会员，无需付费订阅。我们采用先进的视频传输技术，确保<strong>播放稳定不卡顿</strong>，让你享受极致流畅的观影体验。作为一个持续成长的平台，我们每天都在努力为观众带来更多优质内容。
                </p>
              </section>

              <section className="mb-14">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-10">
                  {[
                    { num: '500+', label: '电影资源' },
                    { num: '8', label: '电影分类' },
                    { num: '100万+', label: '月活跃用户' },
                    { num: '4.9', label: '用户评分' },
                  ].map((stat, idx) => (
                    <div key={idx} className="text-center p-4 rounded-xl bg-background-100 border border-background-200/70">
                      <p className="text-xl md:text-2xl font-bold text-primary-400 font-heading">{stat.num}</p>
                      <p className="text-xs text-foreground-400 mt-1">{stat.label}</p>
                    </div>
                  ))}
                </div>
              </section>

              <section className="mb-14">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-50 font-heading mb-2">我们的优势</h2>
                <p className="text-sm text-foreground-400 mb-6">选择快看影视的理由</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { icon: 'ri-movie-2-line', title: '海量片源', desc: '拥有超过500部高清电影资源，涵盖各种类型和题材，每日持续更新最新电影资源。' },
                    { icon: 'ri-4k-line', title: '高清画质', desc: '支持1080P超清画质播放，先进的视频编码技术为你带来影院级别的视觉享受。' },
                    { icon: 'ri-speed-up-line', title: '流畅秒播', desc: '采用CDN加速和P2P技术，确保电影播放流畅不卡顿、不缓冲，打开即看。' },
                    { icon: 'ri-shield-check-line', title: '安全免费', desc: '无需注册、无需付费，完全免费使用。无恶意广告，严格保护你的隐私安全。' },
                  ].map((item, idx) => (
                    <div key={idx} className="p-5 rounded-xl bg-background-100 border border-background-200/70">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500/10 mb-3">
                        <i className={`${item.icon} text-xl text-primary-400`}></i>
                      </div>
                      <h3 className="text-base font-semibold text-foreground-100 mb-1">{item.title}</h3>
                      <p className="text-sm text-foreground-400 leading-relaxed">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </section>

              <section id="testimonials" className="mb-14 scroll-mt-20">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-50 font-heading mb-2">用户口碑</h2>
                <p className="text-sm text-foreground-400 mb-6">听听我们的用户怎么说</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {testimonials.map((t, idx) => (
                    <div key={idx} className="p-5 rounded-xl bg-background-100 border border-background-200/70">
                      <div className="flex items-center gap-3 mb-3">
                        <img
                          src={t.avatar}
                          alt={t.name}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                        <div>
                          <p className="text-sm font-medium text-foreground-100">{t.name}</p>
                          <div className="flex gap-0.5 mt-0.5">
                            {Array.from({ length: t.rating }).map((_, i) => (
                              <i key={i} className="ri-star-fill text-xs text-primary-400"></i>
                            ))}
                          </div>
                        </div>
                      </div>
                      <p className="text-sm text-foreground-400 leading-relaxed">{t.comment}</p>
                    </div>
                  ))}
                </div>
              </section>

              <section className="mb-14">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-50 font-heading mb-2">我们的发展历程</h2>
                <p className="text-sm text-foreground-400 mb-6">从创立至今的成长足迹</p>
                <div className="space-y-6">
                  {[
                    { year: '2023年6月', title: '平台正式上线', desc: '快看影视网站正式发布，首批上线200部高清电影资源，开启免费观影新时代。' },
                    { year: '2023年12月', title: '片库突破300部', desc: '电影资源持续扩充，月活跃用户突破30万，成为国内新兴电影平台。' },
                    { year: '2024年6月', title: '技术全面升级', desc: '升级CDN加速技术和播放引擎，实现全站电影秒播不卡顿，用户体验大幅提升。' },
                    { year: '2025年至今', title: '持续成长', desc: '片库已超过500部高清电影，月活跃用户突破100万，快看影视正朝着更好的方向不断迈进。' },
                  ].map((item, idx) => (
                    <div key={idx} className="flex gap-4">
                      <div className="flex flex-col items-center">
                        <div className="w-3 h-3 rounded-full bg-primary-500 flex-shrink-0 mt-1.5"></div>
                        {idx < 3 && <div className="w-px flex-1 bg-background-300 mt-1"></div>}
                      </div>
                      <div className="pb-2">
                        <span className="text-xs text-primary-400 font-medium">{item.year}</span>
                        <h3 className="text-base font-semibold text-foreground-100 mt-0.5">{item.title}</h3>
                        <p className="text-sm text-foreground-400 mt-1 leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              <section id="contact" className="scroll-mt-20">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-50 font-heading mb-2">联系我们</h2>
                <p className="text-sm text-foreground-400 mb-6">如有任何问题或建议，欢迎通过以下方式联系我们</p>
                <div className="p-6 rounded-xl bg-background-100 border border-background-200/70">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500/10 flex-shrink-0">
                        <i className="ri-mail-line text-lg text-primary-400"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-100">电子邮件</p>
                        <a href="mailto:service@kuikanys.com" className="text-sm text-foreground-400 hover:text-primary-400 transition-colors cursor-pointer">
                          service@kuikanys.com
                        </a>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500/10 flex-shrink-0">
                        <i className="ri-wechat-line text-lg text-primary-400"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-100">微信公众号</p>
                        <p className="text-sm text-foreground-400">快看影视官方</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500/10 flex-shrink-0">
                        <i className="ri-weibo-line text-lg text-primary-400"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-100">新浪微博</p>
                        <a href="https://weibo.com/kuikanys" target="_blank" rel="noopener noreferrer" className="text-sm text-foreground-400 hover:text-primary-400 transition-colors cursor-pointer">
                          @快看影视官方
                        </a>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500/10 flex-shrink-0">
                        <i className="ri-time-line text-lg text-primary-400"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-100">服务时间</p>
                        <p className="text-sm text-foreground-400">周一至周日 9:00 - 22:00</p>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <section className="mt-14 pt-10 border-t border-background-200/70">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  <p className="text-sm text-foreground-400">想发现更多精彩电影？</p>
                  <Link
                    to="/"
                    className="px-6 py-2.5 rounded-lg bg-primary-500 text-background-950 text-sm font-semibold hover:bg-primary-400 transition-all duration-200 whitespace-nowrap cursor-pointer"
                  >
                    返回首页浏览电影
                  </Link>
                </div>
              </section>
            </div>
          </div>
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}